import { get, getDatabase, ref, set } from 'firebase/database';
import { recordActivity } from './activityApi';
import {
    PARKING_QUEUE_CONFIG_PATH,
    ParkingQueueConfig,
    QUEUE_GROUPINGS,
    QUEUE_SORTS,
    parseParkingQueueConfig,
} from '../types/parkingQueueConfig';
import { requireAdmin } from './adminGuard';

/**
 * `global/parkingQueue`, read once per app session and kept, like the
 * customer type. `saveParkingQueueConfig` replaces the cached value so the
 * settings tab and the queue page agree without a reload. `deviationFor` in
 * parkingQueueApi reads the node fresh instead, so the deviation check never
 * uses a stale order.
 */
let cached: Promise<ParkingQueueConfig> | null = null;

export function getParkingQueueConfig(): Promise<ParkingQueueConfig> {
    if (!cached) {
        cached = get(ref(getDatabase(), PARKING_QUEUE_CONFIG_PATH))
            .then(snapshot => parseParkingQueueConfig(snapshot.val()))
            .catch(err => {
                console.error('Failed to read parking queue config', err);
                cached = null;
                return parseParkingQueueConfig(null);
            });
    }
    return cached;
}

/** Fresh read, bypassing the session cache. */
export async function fetchParkingQueueConfig(): Promise<ParkingQueueConfig> {
    const snapshot = await get(ref(getDatabase(), PARKING_QUEUE_CONFIG_PATH));
    return parseParkingQueueConfig(snapshot.val());
}

export async function saveParkingQueueConfig(config: ParkingQueueConfig): Promise<void> {
    const db = getDatabase();
    await requireAdmin(db, 'ändra köordningen');
    if (!QUEUE_GROUPINGS.includes(config.grouping) || !QUEUE_SORTS.includes(config.sort)) {
        throw new Error('Ogiltig köordning');
    }
    const value: ParkingQueueConfig = { grouping: config.grouping, sort: config.sort };
    try {
        await set(ref(db, PARKING_QUEUE_CONFIG_PATH), value);
        cached = Promise.resolve(value);
        recordActivity('settings.updated');
    } catch (err) {
        console.error('Failed to save parking queue config', err);
        throw new Error('Kunde inte spara köordningen. Försök igen.');
    }
}

/** Test hook: forget the cached value. */
export function resetParkingQueueConfigCache(): void {
    cached = null;
}
